import React, {
    useState,
    useEffect,
    useRef,
    FormEvent,
    MouseEventHandler,
    useCallback,
} from 'react';
import isElectron from 'is-electron';
import { twMerge } from 'tailwind-merge';
import BaseInput from 'flow/components/ui-components/BaseInput';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import { getAutoLogin } from 'flow/utils/browser-utils';
import SignInHelperDialog from 'flow/components/modals/dialogs/SignInHelperDialog';
import SignInHelperDialogEtisalat from 'flow/components/modals/dialogs/SignInHelperDialogEtisalat';
import Button from './ui-components/Button';
import ExternalSigninButton from 'flow/components/buttons/ExternalSigninButton';
import CancelExternalSigninDialog from 'flow/components/dialogs/CancelExternalSignInDialog';
import {
    getShouldShowSingleSignOnButton,
    getHasAnySingleSignOn,
} from 'flow/utils/brand-utils';
import { SYNC_SOURCE } from 'flow/utils/constants/app-constants';
import DropdownMenuComponent from './ui-components/DropdownMenuComponent';
import { LocalesData } from 'flow/utils/locales-data';
import Socialintegration from 'flow/api/socialintegration';
import useAppContext from 'flow/hooks/useAppContext';
import BrandLogo from './nav/BrandLogo';
import { useAuthService } from 'flow/authservice/useAuthService';
import Loading from './ui-components/Loading';
import { authServiceSignIn } from 'flow/actions/app-actions';
import { useAppDispatch } from 'flow/hooks/useRedux';
import UaePassSignIn from './uae-pass/UaePassSignIn';
import { useUserRegionData } from 'flow/hooks/region-lookup/useUserRegionData';
import { createTypedMap, TypedMap } from 'flow/types/TypedMap';
import { LoginData, SourceType } from 'flow/types/region-lookup/LoginData';
import UserRegionDropdown from './region-lookup/UserRegionDropdown';
import { SocialLoginType } from 'flow/types/region-lookup/UserRegion';
import { isUaePassEnabled } from 'flow/constants/uae-pass/uae-pass-constants';

interface SignInFormProps {
    handleSignIn: (username: string, password: string) => void;
    password?: string;
    username?: string;
    onUsernameChanged: (val: string) => void;
    onPasswordChanged: (val: string) => void;
    loginData: TypedMap<LoginData>;
    passwordUrl?: string;
    onLinkClick?: MouseEventHandler<HTMLAnchorElement>;
    externalSigninData?: TypedMap<Partial<LoginData>>;
    allowedVersion?: boolean;
    tooManyInstances?: boolean;
    isCtiEmbedded?: boolean;
    isTeamsEmbedded?: boolean;
    startGoogleSignIn?: () => void;
    startMicrosoftSignIn?: () => void;
    startOidcSignIn?: (appRegId?: string) => void;
    cancelExternalSignIn?: () => void;
    showOtherSignInForm?: boolean;
    setShowOtherSignInForm?: (show: boolean) => void;
    enabledOidcSignIn?: boolean;
    setEnabledOidcSignIn?: (enabled: boolean) => void;
    usernameOther?: string;
    onUsernameOtherChanged?: (username: string) => void;
    onLanguageChange?: (language: string) => void;
}

const SignInForm: React.FC<SignInFormProps> = ({
    handleSignIn,
    password = '',
    username = '',
    onUsernameChanged,
    onPasswordChanged,
    loginData,
    passwordUrl,
    onLinkClick,
    externalSigninData = createTypedMap<Partial<LoginData>>(),
    allowedVersion = true,
    tooManyInstances = false,
    isCtiEmbedded = false,
    isTeamsEmbedded = false,
    startGoogleSignIn,
    startMicrosoftSignIn,
    startOidcSignIn,
    cancelExternalSignIn,
    showOtherSignInForm = false,
    setShowOtherSignInForm,
    enabledOidcSignIn = true,
    setEnabledOidcSignIn,
    usernameOther = '',
    onUsernameOtherChanged,
    onLanguageChange,
}) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const [showHelperDialog, setShowHelperDialog] = useState(false);
    const usernameRef = useRef<HTMLInputElement>(null);
    const usernameOtherRef = useRef<HTMLInputElement>(null);
    const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [pendingSignIn, setPendingSignIn] = useState<{
        username: string;
        password: string;
    } | null>(null);
    const [pendOidcUserName, setPendOidcUserName] = useState<string | null>(
        null,
    );
    const [isUaePassFlowActive, setIsUaePassFlowActive] = useState(false);
    const {
        authLogin,
        isAuthenticated,
        token,
        isAuthLoading,
        isAuthServiceEnabled,
    } = useAuthService();

    const {
        isRegionCheckEnabled,
        fetchRegionData,
        regionData,
        clearRegionData,
        isEndpointUpdated,
        updateRegionData,
        isRegionsLoading,
    } = useUserRegionData();

    const hasCleanedStaleSocialLogin = useRef(false);
    const userInitiatedSocialLogin = useRef(false);

    useEffect(() => {
        if (
            !hasCleanedStaleSocialLogin.current &&
            !userInitiatedSocialLogin.current &&
            isRegionCheckEnabled &&
            regionData?.socialLoginType &&
            !getAutoLogin()
        ) {
            hasCleanedStaleSocialLogin.current = true;
            updateRegionData({
                socialLoginType: undefined,
                socialSelectedUrl: undefined,
            });
        }
    }, [isRegionCheckEnabled, regionData?.socialLoginType, updateRegionData]);

    useEffect(() => {
        if (focusTimeoutRef.current) {
            clearTimeout(focusTimeoutRef.current);
        }

        focusTimeoutRef.current = setTimeout(() => {
            if (showOtherSignInForm) {
                usernameOtherRef.current?.focus();
            } else {
                usernameRef.current?.focus();
            }
        }, 50);

        return () => {
            if (focusTimeoutRef.current) {
                clearTimeout(focusTimeoutRef.current);
            }
        };
    }, [showOtherSignInForm]);

    useEffect(() => {
        if (isAuthenticated && token && isAuthServiceEnabled) {
            dispatch(authServiceSignIn(token, () => {}));
        }
    }, [token, isAuthenticated, isAuthServiceEnabled, dispatch]);

    const showError = loginData.get('error');
    const showSpinner =
        loginData.get('waiting') || loginData.get('error') === false;

    useEffect(() => {
        if (
            pendingSignIn &&
            regionData?.userName === pendingSignIn.username &&
            isEndpointUpdated
        ) {
            const { username, password } = pendingSignIn;
            setPendingSignIn(null);
            handleSignIn(username, password);
        }
    }, [regionData, pendingSignIn, isEndpointUpdated, handleSignIn]);

    const getErrorText = (
        loginData: TypedMap<Partial<LoginData>> | TypedMap<LoginData>,
    ) => {
        if (loginData.get('networkFail')) {
            return i18next.t('signin.error.network');
        } else if (loginData.get('loginFail')) {
            return i18next.t('signin.error.credentials');
        }
        if (loginData.get('loginTokenFail')) {
            return i18next.t('signin.error.credentials_expired');
        }
        if (loginData.get('serverFail')) {
            return i18next.t('signin.error.server');
        } else if (loginData.get('userEmailFail')) {
            return i18next.t('signin.error.usernotfound');
        } else if (loginData.get('openidFail')) {
            return i18next.t('signin.error.openid');
        }

        return i18next.t('signin.error.unknown');
    };

    const updateRegionDataSocialLogin = (socialLoginType: SocialLoginType) => {
        userInitiatedSocialLogin.current = true;
        updateRegionData({
            ...regionData,
            socialLoginType,
            url: regionData?.socialSelectedUrl,
        });
    };

    // SSO Functions
    const renderGoogleSignInButton = () => {
        if (!startGoogleSignIn) return null;

        return (
            getShouldShowSingleSignOnButton('google') && (
                <div className="mb-3">
                    <ExternalSigninButton
                        type="google"
                        clickHandler={() => {
                            updateRegionDataSocialLogin('google');
                            startGoogleSignIn();
                        }}
                    />
                    {renderExternalSignInError(SYNC_SOURCE.google)}
                </div>
            )
        );
    };

    const renderMicrosoftSignInButton = () => {
        if (!startMicrosoftSignIn) return null;

        return (
            getShouldShowSingleSignOnButton('microsoft') && (
                <div className="mb-3">
                    <ExternalSigninButton
                        type="microsoft"
                        clickHandler={() => {
                            updateRegionDataSocialLogin('microsoft');
                            startMicrosoftSignIn();
                        }}
                    />
                    {renderExternalSignInError(SYNC_SOURCE.microsoft)}
                </div>
            )
        );
    };

    const renderOidcSignInButton = () => {
        if (!setShowOtherSignInForm) return null;

        return (
            getShouldShowSingleSignOnButton('oidc') && (
                <div className="mb-3">
                    <ExternalSigninButton
                        type="oidc"
                        clickHandler={changeShowOtherSignInForm}
                    />
                    {renderExternalSignInError(SYNC_SOURCE.oidc)}
                </div>
            )
        );
    };

    const renderExternalSignInError = (syncSource: string) => {
        return (
            externalSigninData.get('syncSource') === syncSource &&
            externalSigninData.get('error') && (
                <div className="mt-2 flex items-center rounded-sm bg-red-100 px-3 py-2 text-sm text-red-600">
                    <span>{getErrorText(externalSigninData)}</span>
                </div>
            )
        );
    };

    const showSSOButtons = () => {
        // Inside a Teams tab only Microsoft is offered, through the Teams popup
        // (startTeamsMicrosoftSignIn); the redirect-based sign-ins can't run there.
        if (isTeamsEmbedded) {
            return true;
        }

        if (IS_UAE_HOST && !isUaePassEnabled) {
            return false;
        }

        if (isCtiEmbedded) {
            return true;
        }

        return isElectron() && getHasAnySingleSignOn();
    };

    const renderExternalSignInSection = () => {
        if (!showSSOButtons()) {
            return null;
        }

        return externalSigninData.get('waiting') ? (
            <CancelExternalSigninDialog
                onClose={() => {
                    userInitiatedSocialLogin.current = false;
                    cancelExternalSignIn?.();
                }}
                open={true}
                syncSource={externalSigninData.get('syncSource') as SourceType}
            />
        ) : (
            <div className="mt-6">
                {!isTeamsEmbedded && renderGoogleSignInButton()}
                {renderMicrosoftSignInButton()}
                {!isTeamsEmbedded && renderOidcSignInButton()}
            </div>
        );
    };

    const renderUaePassSection = () => {
        if (!isUaePassEnabled) {
            return null;
        }

        return (
            <div className="mt-6">
                <UaePassSignIn onFlowActiveChange={setIsUaePassFlowActive} />
            </div>
        );
    };

    const renderSignInFooter = () => (
        <>
            {tooManyInstances && (
                <div className="mt-6 rounded-sm bg-yellow-100 px-3 py-2 text-sm text-yellow-800">
                    {i18next.t('signin.form.tooManyInstances')}
                </div>
            )}

            <hr className="my-6 w-full border-gray-200" />
            <div className="flex h-12 w-full items-center justify-between">
                {isRegionCheckEnabled && showSSOButtons() && (
                    <div className="flex-1 items-start justify-center">
                        {!isRegionsLoading ? (
                            <UserRegionDropdown />
                        ) : (
                            <div className="[&>div]:!items-start [&>div]:!self-start">
                                <Loading size={16} disableTimeout={true} />
                            </div>
                        )}
                    </div>
                )}
                {onLanguageChange && !isRegionCheckEnabled && (
                    <div>
                        <DropdownMenuComponent
                            title={
                                localeObject?.description ||
                                i18next.t('general.language', 'Language')
                            }
                            options={languageOptions}
                            onItemSelected={onLanguageChange}
                            menuButtonClassName="w-auto bg-transparent"
                        />
                    </div>
                )}
                <div className="flex-1 text-right">
                    <button
                        type="button"
                        className="hover:text-main rounded px-2 py-1 text-sm text-gray-600"
                        onClick={() => setShowHelperDialog(true)}
                    >
                        {i18next.t('signin.helper.help')}
                    </button>
                </div>
                {showHelperDialog &&
                    (IS_UAE_HOST ? (
                        <SignInHelperDialogEtisalat
                            open={showHelperDialog}
                            handleClose={() => setShowHelperDialog(false)}
                            onLinkClick={onLinkClick}
                        />
                    ) : (
                        <SignInHelperDialog
                            open={showHelperDialog}
                            handleClose={() => setShowHelperDialog(false)}
                            onLinkClick={onLinkClick}
                        />
                    ))}
            </div>
        </>
    );

    const displayVersionNotAllowed = () => {
        return (
            <div className="mb-6 rounded-sm bg-red-100 px-4 py-3 text-red-600">
                <div className="mb-2 text-base font-semibold">
                    {i18next.t('signin.blacklist.version_expired')}
                </div>
                <div className="text-sm">
                    {i18next.t('signin.blacklist.contact_admin')}
                </div>
            </div>
        );
    };

    // OIDC Functions
    const changeShowOtherSignInForm = () => {
        if (showOtherSignInForm && setEnabledOidcSignIn) {
            setEnabledOidcSignIn(true);
        }
        if (setShowOtherSignInForm) {
            setShowOtherSignInForm(!showOtherSignInForm);
            if (isRegionCheckEnabled && clearRegionData) {
                clearRegionData();
            }
        }
        if (onUsernameOtherChanged) {
            onUsernameOtherChanged('');
        }
    };

    const changeEnabledOidcSignIn = useCallback(
        (enabled: boolean) => {
            if (setEnabledOidcSignIn) {
                setEnabledOidcSignIn(enabled);
            }
        },
        [setEnabledOidcSignIn],
    );

    const handleOidcSignIn = useCallback(
        (usernameOther: string) => {
            Socialintegration.getAppRegistration(
                {
                    onResult(appRegistrationId: string | null) {
                        if (appRegistrationId != null && startOidcSignIn) {
                            startOidcSignIn(appRegistrationId);
                        } else {
                            changeEnabledOidcSignIn(false);
                        }
                    },
                    onError() {
                        changeEnabledOidcSignIn(false);
                    },
                    onClientError() {
                        changeEnabledOidcSignIn(false);
                    },
                },
                usernameOther,
            );
        },
        [startOidcSignIn, changeEnabledOidcSignIn],
    );

    const prepareOidcSignIn = (
        usernameOther: string,
        event: FormEvent | null = null,
    ) => {
        event?.preventDefault();

        if (!startOidcSignIn) return;
        const { userName } = regionData || {};

        if (isRegionCheckEnabled && usernameOther !== userName) {
            setPendOidcUserName(usernameOther);
            fetchRegionData(usernameOther, true);
        } else {
            handleOidcSignIn(usernameOther);
        }
    };

    useEffect(() => {
        const { userName, isOidcLogin } = regionData || {};
        if (
            pendOidcUserName &&
            userName === pendOidcUserName &&
            isEndpointUpdated &&
            isOidcLogin
        ) {
            handleOidcSignIn(pendOidcUserName);
            setPendOidcUserName(null);
        }
    }, [
        pendOidcUserName,
        regionData,
        isRegionCheckEnabled,
        fetchRegionData,
        isEndpointUpdated,
        handleOidcSignIn,
    ]);

    const renderOtherSignInForm = () => {
        if (!showOtherSignInForm) return null;

        const showSpinner =
            loginData.get('waiting') || loginData.get('error') === false;

        return (
            <div className="w-full">
                <form
                    className="flex w-full flex-col gap-5"
                    onSubmit={(event) =>
                        enabledOidcSignIn
                            ? prepareOidcSignIn(usernameOther, event)
                            : changeShowOtherSignInForm()
                    }
                >
                    <div className="flex justify-start">
                        <button
                            type="button"
                            onClick={changeShowOtherSignInForm}
                            className="flex items-center text-gray-600 hover:text-gray-800"
                        >
                            <span className="me-2">←</span>
                            {i18next.t('common.back', 'Back')}
                        </button>
                    </div>

                    <div className="text-center">
                        <h2 className="text-xl font-semibold">
                            {i18next.t('signin.oidc.enter.username')}
                        </h2>
                    </div>

                    {/* Username Other Input */}
                    <BaseInput
                        id="username-other"
                        autoComplete="username-other"
                        value={usernameOther}
                        onChange={(e) =>
                            onUsernameOtherChanged?.(e.target.value)
                        }
                        ref={usernameOtherRef}
                        placeholder={i18next.t('signin.email_phone')}
                        disabled={!enabledOidcSignIn}
                    />

                    {/* External sign-in waiting dialog */}
                    {externalSigninData.get('waiting') && (
                        <CancelExternalSigninDialog
                            onClose={cancelExternalSignIn || (() => {})}
                            open={true}
                            syncSource={
                                externalSigninData.get(
                                    'syncSource',
                                ) as SourceType
                            }
                        />
                    )}

                    {/* OIDC Error */}
                    {renderExternalSignInError('oidc')}

                    {/* Buttons */}
                    {enabledOidcSignIn ? (
                        <Button
                            type="submit"
                            className="bg-main text-textOnBrand"
                            isLoading={showSpinner}
                        >
                            {i18next.t('signin.oidc.next')}
                        </Button>
                    ) : (
                        <div className="text-center">
                            <div className="mb-4 font-semibold">
                                {i18next.t('signin.oidc.no.login.found')}
                            </div>
                            <Button
                                type="button"
                                onClick={changeShowOtherSignInForm}
                                className="bg-gray-500 text-white"
                            >
                                {i18next.t('signin.oidc.back')}
                            </Button>
                        </div>
                    )}
                </form>
            </div>
        );
    };

    const userLanguage = (i18next.language || 'en') as keyof typeof LocalesData;

    const languageOptions = Object.entries(LocalesData).map(
        ([code, locale]) => ({
            key: code,
            label: locale.description,
        }),
    );

    const localeObject = LocalesData[userLanguage];

    const renderAuthServiceLogin = () => (
        <div className="relative">
            <span className="bg-yellow absolute top-2 -right-2 rounded-lg px-1.5 py-1 text-[8px] font-bold text-gray-800 uppercase dark:text-white">
                {'Testing'}
            </span>
            <button
                className="bg-main text-textOnBrand mt-4 w-full rounded p-2 text-sm"
                onClick={authLogin}
                aria-label={i18next.t('signin.authservice.button')}
                aria-describedby="authservice-login-button-description"
            >
                {i18next.t('signin.authservice.button')}
            </button>
        </div>
    );

    const handleSignInProcess = useCallback(
        (event: FormEvent) => {
            event.preventDefault();
            userInitiatedSocialLogin.current = false;
            const fullUserName =
                (username || regionData?.userName)?.trim() || '';
            const { isOidcLogin, userName } = regionData || {};

            if (
                isRegionCheckEnabled &&
                fullUserName &&
                fullUserName !== userName &&
                !isOidcLogin
            ) {
                setPendingSignIn({
                    username: fullUserName,
                    password,
                });
                fetchRegionData(fullUserName);
            } else {
                handleSignIn(fullUserName, password);
            }
        },
        [
            regionData,
            username,
            password,
            isRegionCheckEnabled,
            fetchRegionData,
            handleSignIn,
        ],
    );

    return !isAuthLoading && !isAuthenticated ? (
        <div
            className={twMerge(
                'flex h-full items-center bg-white px-4',
                /* UAE Pass owned this screen's dark background before it moved inline. */
                isUaePassEnabled && 'dark:darken-25',
            )}
        >
            <div className="mx-auto flex w-full max-w-md flex-col">
                {!isUaePassFlowActive && (
                    <BrandLogo
                        type="logo48"
                        className="mb-4"
                        isPreLogin={true}
                    />
                )}
                {!allowedVersion && displayVersionNotAllowed()}

                {allowedVersion &&
                    showOtherSignInForm &&
                    renderOtherSignInForm()}

                {allowedVersion && !showOtherSignInForm && (
                    <>
                        {/* Hidden rather than overlaid, so a running flow can't be submitted behind it. */}
                        {!isUaePassFlowActive && (
                            <>
                                <form
                                    className="flex w-full flex-col gap-5"
                                    onSubmit={handleSignInProcess}
                                >
                                    <BaseInput
                                        id="username"
                                        autoComplete="username"
                                        value={username || regionData?.userName}
                                        onChange={(e) => {
                                            const newUserName = e.target.value;
                                            onUsernameChanged(newUserName);

                                            if (
                                                regionData &&
                                                newUserName !==
                                                    regionData?.userName
                                            ) {
                                                clearRegionData();
                                            }
                                        }}
                                        ref={usernameRef}
                                        placeholder={i18next.t(
                                            'signin.email_phone',
                                        )}
                                    />
                                    <BaseInput
                                        id="password"
                                        type="password"
                                        autoComplete="current-password"
                                        value={password}
                                        onChange={(e) =>
                                            onPasswordChanged(e.target.value)
                                        }
                                        placeholder={i18next.t(
                                            'signin.password',
                                        )}
                                    />
                                    {showError && (
                                        <div className="text-red mt-1 rounded-sm bg-red-100 px-3 py-2 text-sm">
                                            {getErrorText(loginData)}
                                        </div>
                                    )}
                                    <div className="flex justify-between">
                                        {passwordUrl && (
                                            <a
                                                href={passwordUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={onLinkClick}
                                                className="text-sm text-blue-800 hover:underline dark:text-blue-500"
                                            >
                                                {i18next.t(
                                                    'signin.forgot_password',
                                                )}
                                            </a>
                                        )}
                                    </div>

                                    <Button
                                        type="submit"
                                        className="bg-main text-textOnBrand"
                                        isLoading={showSpinner}
                                    >
                                        {i18next.t('signin.signin')}
                                    </Button>
                                </form>

                                {isAuthServiceEnabled &&
                                    renderAuthServiceLogin()}

                                {renderExternalSignInSection()}
                            </>
                        )}

                        {renderUaePassSection()}

                        {!isUaePassFlowActive && renderSignInFooter()}
                    </>
                )}
            </div>
        </div>
    ) : (
        <Loading size={64} />
    );
};

export default SignInForm;
